/**
 * Note Repository
 * Data access for notes
 * 
 * @module repositories/noteRepository
 */

import { supabase } from '../services/supabase';
import { ApiError } from '../lib/errors';
import { logger } from '../lib/logger';

// =============================================================================
// Types
// =============================================================================

export interface NoteRow {
    id: string;
    page_id: string;
    type: string;
    content: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    color_theme: string | null;

    meta: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
}

export interface NoteInsert {
    id: string;
    page_id: string;
    type: string;
    content?: string;
    x: number;
    y: number;
    width?: number;
    height?: number;
    color_theme?: string;
    meta?: Record<string, unknown>;
}

export interface NoteUpdate {
    content?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    color_theme?: string;
    meta?: Record<string, unknown>;
}

// =============================================================================
// Repository
// =============================================================================

export const noteRepository = {
    /**
     * Find all notes for a page
     */
    async findByPageId(pageId: string): Promise<NoteRow[]> {
        logger.debug('[NoteRepo] findByPageId', { pageId });

        const { data, error } = await supabase
            .from('notes')
            .select('*')
            .eq('page_id', pageId);

        if (error) {
            throw new ApiError('Failed to fetch notes', {
                statusCode: 500,
                context: { pageId, error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Find note by ID
     */
    async findById(id: string): Promise<NoteRow | null> {
        const { data, error } = await supabase
            .from('notes')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null;
            }
            throw new ApiError('Failed to fetch note', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Create a new note
     */
    async create(note: NoteInsert): Promise<NoteRow> {
        logger.debug('[NoteRepo] create', { pageId: note.page_id, type: note.type });

        const { data, error } = await supabase
            .from('notes')
            .insert({
                ...note,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to create note', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data;
    },

    /**
     * Update a note
     */
    async update(id: string, updates: NoteUpdate): Promise<NoteRow> {
        const { data, error } = await supabase
            .from('notes')
            .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new ApiError('Failed to update note', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }

        return data;
    },

    /**
     * Batch upsert notes
     */
    async upsertMany(notes: NoteInsert[]): Promise<NoteRow[]> {
        if (notes.length === 0) return [];

        logger.debug('[NoteRepo] upsertMany', { count: notes.length });

        const { data, error } = await supabase
            .from('notes')
            .upsert(
                notes.map(n => ({
                    ...n,
                    updated_at: new Date().toISOString(),
                })),
                { onConflict: 'id' }
            )
            .select();

        if (error) {
            throw new ApiError('Failed to upsert notes', {
                statusCode: 500,
                context: { error: error.message },
            });
        }

        return data ?? [];
    },

    /**
     * Delete a note
     */
    async delete(id: string): Promise<void> {
        const { error } = await supabase
            .from('notes')
            .delete()
            .eq('id', id);

        if (error) {
            throw new ApiError('Failed to delete note', {
                statusCode: 500,
                context: { id, error: error.message },
            });
        }
    },

    /**
     * Delete multiple notes
     */
    async deleteMany(ids: string[]): Promise<void> {
        if (ids.length === 0) return;

        const { error } = await supabase
            .from('notes')
            .delete()
            .in('id', ids);

        if (error) {
            throw new ApiError('Failed to delete notes', {
                statusCode: 500,
                context: { ids, error: error.message },
            });
        }
    },
};
